import React from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { Popup } from '@/components/Popup';
import type {
  DiscoverBookmaker,
  DiscoverLeagueBookmakersResponse,
} from '@/types/leagueBookmakers';
import styles from '@/pages/admin/LeagueBookmakersTab.module.scss';

interface DiscoverModalProps {
  open: boolean;
  onClose: () => void;
  isLoading: boolean;
  error: string | null;
  data: DiscoverLeagueBookmakersResponse['data'] | null;
  draftIds: number[];
  onAddToPriority: (dbId: number) => void;
}

// Sort by 1X2 coverage descending so the most useful bookmakers float to the top.
function sortBookmakers(bookmakers: DiscoverBookmaker[]): DiscoverBookmaker[] {
  return [...bookmakers].sort(
    (a, b) => b.coverage.matchesWithMatchWinner - a.coverage.matchesWithMatchWinner,
  );
}

export const DiscoverModal: React.FC<DiscoverModalProps> = ({
  open,
  onClose,
  isLoading,
  error,
  data,
  draftIds,
  onAddToPriority,
}) => {
  const { t } = useTranslation('admin');

  const sorted = data ? sortBookmakers(data.bookmakers) : [];

  return (
    <Popup open={open} onClose={onClose}>
      <div className={styles.discoverModal}>
        <div className={styles.discoverHeader}>
          <h3 className={styles.discoverTitle}>
            {data
              ? t('discoverModalTitle', { league: data.league.name })
              : t('discoverFromApi')}
          </h3>
          {data && (
            <span className={styles.discoverMeta}>
              {t('discoverSampledFixtures', { count: data.sampledFixtures })}
            </span>
          )}
        </div>

        {data?.warnings && data.warnings.length > 0 && (
          <div className={styles.discoverWarnings}>
            <strong>{t('discoverWarnings')}:</strong>
            {data.warnings.map((w, i) => (
              <span key={i}>{w}</span>
            ))}
          </div>
        )}

        {isLoading && (
          <div className={styles.loadingCenter}>
            <div className="spinner" />
            <span>{t('discoverLoading')}</span>
          </div>
        )}

        {error && !isLoading && (
          <div className={`${styles.message} ${styles.error}`}>
            <span>{error}</span>
          </div>
        )}

        {!isLoading && !error && data && (
          <div className={styles.discoverTableWrap}>
            <table className={styles.discoverTable}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>DB</th>
                  <th>Status</th>
                  <th>{t('coverageMatchWinner')}</th>
                  <th>{t('coverageExactScore')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {sorted.map((b) => {
                  const isInDraft = b.dbId !== null && draftIds.includes(b.dbId);
                  const canAdd =
                    b.existsInDb && b.isEnabled && b.dbId !== null && !isInDraft;
                  return (
                    <tr key={b.externalId}>
                      <td className={styles.discoverNameCell}>{b.name}</td>
                      <td className={styles.discoverBadgeCell}>
                        {b.existsInDb ? (
                          <span className={`${styles.badge} ${styles.badgeNeutral}`}>
                            ✓ {t('existsInDb')}
                          </span>
                        ) : (
                          <span className={`${styles.badge} ${styles.badgeWarning}`}>
                            {t('missingInDb')}
                          </span>
                        )}
                      </td>
                      <td className={styles.discoverBadgeCell}>
                        {b.existsInDb && !b.isEnabled && (
                          <span className={`${styles.badge} ${styles.badgeWarning}`}>
                            {t('bookmakerDisabledShort')}
                          </span>
                        )}
                        {isInDraft && b.currentPriority !== null && (
                          <span className={`${styles.badge} ${styles.badgeAccent}`}>
                            {t('priorityBadge', { n: b.currentPriority })}
                          </span>
                        )}
                        {b.isCurrentlyMapped && !isInDraft && (
                          <span className={`${styles.badge} ${styles.badgeSuccess}`}>
                            {t('mappedBadge')}
                          </span>
                        )}
                      </td>
                      <td className={styles.discoverCoverageCell}>
                        {b.coverage.matchesWithMatchWinner}/{data.sampledFixtures}
                      </td>
                      <td className={styles.discoverCoverageCell}>
                        {b.coverage.matchesWithExactScore}/{data.sampledFixtures}
                        {' '}({b.coverage.totalExactScoreValues})
                      </td>
                      <td className={styles.discoverActionCell}>
                        {canAdd ? (
                          <button
                            type="button"
                            className={styles.addBtn}
                            onClick={() => onAddToPriority(b.dbId!)}
                          >
                            + {t('addToPriority')}
                          </button>
                        ) : b.existsInDb && !b.isEnabled ? (
                          <NavLink
                            to="/admin/bookmakers"
                            className={styles.discoverDisabledHint}
                          >
                            {t('bookmakerDisabledHint')}
                          </NavLink>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Popup>
  );
};

export default DiscoverModal;
